import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Build as BuildIcon,
  ShoppingCart as ShoppingCartIcon,
  Store as StoreIcon,
  AccountBalance as AccountBalanceIcon,
  TwoWheeler as TwoWheelerIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  CloudSync as CloudSyncIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Colori del brand Drivio Mobility Hub
const brandColors = {
  primary: '#DB1111', // Rosso principale
  secondary: '#0066CC', // Blu tecnologia
  accent: '#FF6B35', // Arancione energia
  dark: '#1a1a1a',
  light: '#f5f5f5'
};

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewScrollPosition, setReviewScrollPosition] = useState(0);
  const [isHoveringReviews, setIsHoveringReviews] = useState(false);
  const reviewsContainerRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);

  const features = [
    {
      icon: <DashboardIcon sx={{ fontSize: 60 }} />,
      title: 'Dashboard Intelligente',
      description: 'Monitora tutte le attività della tua officina con una dashboard completa. Visualizza statistiche in tempo reale, vendite, manutenzioni attive e molto altro.',
      color: brandColors.primary,
      image: '/Dashboard.png',
      details: [
        'Statistiche in tempo reale su vendite e manutenzioni',
        'Grafici interattivi per analisi dei dati',
        'Notifiche e promemoria automatici',
        'Vista personalizzabile in base alle tue esigenze',
        'Report giornalieri, settimanali e mensili',
        'Indicatori KPI per monitorare le performance'
      ]
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 60 }} />,
      title: 'Gestione Clienti',
      description: 'Archivia e gestisci tutti i dati dei tuoi clienti. Traccia la storia dei veicoli, le manutenzioni effettuate e mantieni un database completo e organizzato.',
      color: brandColors.secondary,
      image: '/Clienti.png',
      details: [
        'Database clienti completo e organizzato',
        'Storia completa dei veicoli per ogni cliente',
        'Tracciamento di tutte le manutenzioni effettuate',
        'Gestione contatti e comunicazioni',
        'Storico acquisti e preferenze',
        'Sistema di note e promemoria personalizzati'
      ]
    },
    {
      icon: <InventoryIcon sx={{ fontSize: 60 }} />,
      title: 'Magazzino Avanzato',
      description: 'Controllo completo del magazzino con tracciamento in tempo reale, movimenti automatici, gestione ordini e integrazione con il listino B2B.',
      color: brandColors.accent,
      image: '/Magazzino.png',
      details: [
        'Tracciamento stock in tempo reale',
        'Movimenti automatici da vendite e manutenzioni',
        'Gestione ordini e consegne',
        'Integrazione completa con catalogo B2B',
        'Alert per scorte minime',
        'Reportistica avanzata su movimenti e giacenze'
      ]
    },
    {
      icon: <BuildIcon sx={{ fontSize: 60 }} />,
      title: 'Gestione Manutenzioni',
      description: 'Organizza e traccia tutte le manutenzioni. Crea preventivi, gestisci sessioni di lavoro, importa articoli e monitora lo stato degli interventi.',
      color: '#9c27b0',
      image: '/Manutenzioni.png',
      details: [
        'Creazione e gestione preventivi dettagliati',
        'Tracciamento stato manutenzioni in tempo reale',
        'Gestione sessioni di lavoro per meccanici',
        'Import automatico articoli dal magazzino',
        'Sistema di notifiche per clienti',
        'Storico completo interventi per veicolo'
      ]
    },
    {
      icon: <ShoppingCartIcon sx={{ fontSize: 60 }} />,
      title: 'Vendite al Banco',
      description: 'Sistema di vendita rapido e intuitivo. Gestisci vendite dirette, carrello, storico vendite e calcolo automatico dei prezzi con manodopera.',
      color: '#d32f2f',
      image: '/Vendite.png',
      details: [
        'Interfaccia di vendita rapida e intuitiva',
        'Gestione carrello multiplo',
        'Calcolo automatico prezzi con manodopera',
        'Storico vendite completo',
        'Gestione metodi di pagamento',
        'Stampa scontrini e ricevute'
      ]
    },
    {
      icon: <StoreIcon sx={{ fontSize: 60 }} />,
      title: 'Shop B2B Integrato',
      description: 'Accesso diretto al catalogo B2B con prezzi dinamici basati sul rank aziendale. Ordini semplificati e gestione automatica delle consegne.',
      color: brandColors.secondary,
      image: '/B2B.png',
      details: [
        'Catalogo B2B completo e aggiornato',
        'Prezzi dinamici basati sul rank aziendale',
        'Sistema di ordini semplificato',
        'Tracciamento consegne automatico',
        'Gestione resi e rimborsi',
        'Integrazione con magazzino locale'
      ]
    },
    {
      icon: <AccountBalanceIcon sx={{ fontSize: 60 }} />,
      title: 'Contabilità Completa',
      description: 'Registro contabile automatico con movimenti generati dalle vendite e manutenzioni. Filtri avanzati, esportazione Excel e stampa PDF professionale.',
      color: '#00796b',
      image: '/Contabilita.png',
      details: [
        'Registro contabile automatico',
        'Movimenti generati da vendite e manutenzioni',
        'Filtri avanzati per ricerca e analisi',
        'Esportazione dati in Excel',
        'Stampa PDF professionale',
        'Report contabili personalizzabili'
      ]
    },
    {
      icon: <TwoWheelerIcon sx={{ fontSize: 60 }} />,
      title: 'Gestione Noleggi',
      description: 'Sistema completo per la gestione dei noleggi di veicoli. Traccia disponibilità, contratti, pagamenti e stato dei mezzi in tempo reale.',
      color: '#5c6bc0',
      image: '/Noleggio.png',
      details: [
        'Gestione completa flotta veicoli',
        'Tracciamento disponibilità in tempo reale',
        'Gestione contratti e documenti',
        'Sistema di pagamenti e scadenze',
        'Storico noleggi per cliente',
        'Reportistica su utilizzo e ricavi'
      ]
    },
    {
      icon: <SettingsIcon sx={{ fontSize: 60 }} />,
      title: 'Configurazione Flessibile',
      description: 'Personalizza il software secondo le tue esigenze. Imposta tariffe manodopera, metodi di pagamento, conti contabili e molto altro.',
      color: '#616161',
      details: [
        'Configurazione tariffe manodopera',
        'Gestione metodi di pagamento',
        'Impostazione conti contabili',
        'Personalizzazione categorie prodotti',
        'Configurazione notifiche',
        'Impostazioni azienda e branding'
      ]
    }
  ];

  const benefits = [
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: brandColors.primary }} />,
      title: 'Velocità',
      description: 'Interfaccia intuitiva e reattiva per operazioni rapide'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: brandColors.primary }} />,
      title: 'Sicurezza',
      description: 'Dati protetti con autenticazione e controllo accessi'
    },
    {
      icon: <CloudSyncIcon sx={{ fontSize: 40, color: brandColors.primary }} />,
      title: 'Sincronizzazione',
      description: 'Dati sempre aggiornati e sincronizzati in tempo reale'
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40, color: brandColors.primary }} />,
      title: 'Reportistica',
      description: 'Analisi dettagliate e report personalizzabili'
    }
  ];

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedFeature(null);
  };

  const reviews = [
    { name: 'Marco Rossi', text: 'Finalmente un software che funziona davvero. La gestione magazzino è perfetta, non perdo più pezzi e ho sempre tutto sotto controllo. Il preventivo per le manutenzioni è velocissimo da fare.' },
    { name: 'Giulia Bianchi', text: 'Uso il software da 8 mesi e ho dimezzato i tempi per gestire le vendite. Il carrello è intuitivo e il calcolo automatico dei prezzi con manodopera mi salva ogni giorno. Consigliatissimo!' },
    { name: 'Luca Ferrari', text: 'La parte contabile è quella che mi ha convinto. Tutti i movimenti si generano automaticamente, non devo più stare a inserire manualmente ogni transazione. Risparmio ore di lavoro.' },
    { name: 'Sara Romano', text: 'Ottimo per tracciare le manutenzioni. Vedo subito cosa ho fatto su ogni bici, quando e quanto ho speso. I clienti apprezzano molto la trasparenza sui lavori effettuati.' },
    { name: 'Andrea Conti', text: 'Il shop B2B integrato è geniale. Ordino direttamente dal software e quando arriva la merce la carico con un click. Non devo più stare a controllare manualmente gli ordini.' },
    { name: 'Elena Marini', text: 'Prima usavo Excel e fogli di carta. Ora tutto è digitale e organizzato. La ricerca clienti è istantanea e ho sempre la storia completa di ogni veicolo a portata di mano.' },
    { name: 'Paolo Greco', text: 'La dashboard mi dà una visione chiara di come va l\'officina. Vedo subito le vendite del giorno, le manutenzioni in corso e lo stato del magazzino. Molto utile per prendere decisioni.' },
    { name: 'Chiara De Luca', text: 'Il sistema di sessioni di lavoro per i meccanici è perfetto. So esattamente quanto tempo impiegano su ogni intervento e posso calcolare i costi in modo preciso. Niente più stime a caso.' },
    { name: 'Francesco Ricci', text: 'Ho provato altri software ma questo è il migliore. L\'interfaccia è pulita, non ci sono funzioni inutili che confondono. Va dritto al punto e funziona bene.' },
    { name: 'Valentina Moretti', text: 'La gestione clienti è completa. Tengo traccia di tutto: dati anagrafici, veicoli, manutenzioni passate, note importanti. Quando un cliente chiama ho subito tutte le info.' },
    { name: 'Simone Galli', text: 'Il calcolo automatico dei prezzi con montaggio incluso è una manna dal cielo. Prima dovevo fare calcoli a mano ogni volta, ora è tutto automatico e preciso al centesimo.' },
    { name: 'Martina Leone', text: 'Uso soprattutto la parte vendite. Il carrello è veloce, i prodotti si trovano subito e lo scontrino si stampa in un attimo. I clienti sono contenti della velocità.' },
    { name: 'Davide Rizzo', text: 'La sincronizzazione in tempo reale è perfetta. Se un meccanico carica una manutenzione, la vedo subito. Non ci sono più ritardi o informazioni non aggiornate.' },
    { name: 'Alessia Costa', text: 'Il report contabile mensile è dettagliatissimo. Esporto in Excel e lo passo al commercialista senza problemi. Ha tutto quello che serve per la contabilità.' },
    { name: 'Matteo Fontana', text: 'La gestione ordini B2B è semplice e chiara. Vedo subito cosa ho ordinato, quando arriva e quanto ho speso. Non perdo più traccia di nulla.' },
    { name: 'Giorgia Caruso', text: 'Il software mi ha aiutato a organizzare meglio l\'officina. Ora so sempre dove sono i pezzi, cosa devo ordinare e quali manutenzioni sono in corso. Molto professionale.' },
    { name: 'Riccardo Marino', text: 'La parte preventivi è ottima. Creo un preventivo in pochi minuti, lo invio al cliente e se accetta lo converto direttamente in manutenzione. Flusso perfetto.' },
    { name: 'Federica Santoro', text: 'Uso il software da un anno e non tornerei mai indietro. Ho tutto centralizzato: clienti, magazzino, vendite, contabilità. Risparmio tempo e faccio meno errori.' },
    { name: 'Alessandro Lombardi', text: 'Il sistema di alert per scorte minime funziona benissimo. Non rimango mai senza pezzi importanti. Il software mi avvisa quando devo ordinare qualcosa.' },
    { name: 'Sofia Barbieri', text: 'La ricerca prodotti è velocissima. Digito il codice o il nome e trovo subito quello che cerco. Anche con centinaia di articoli in magazzino non ho problemi.' },
    { name: 'Nicola Ferrara', text: 'La gestione noleggi è completa. Traccio disponibilità, contratti e pagamenti senza problemi. I clienti possono vedere subito cosa è disponibile.' },
    { name: 'Elisa Gentile', text: 'Il software è intuitivo, l\'ho imparato in un giorno. Non serve un corso per usarlo, è tutto logico e ben organizzato. Anche i miei dipendenti lo usano senza problemi.' },
    { name: 'Stefano Bruno', text: 'La parte contabile è quella che mi ha convinto di più. Tutti i movimenti si generano automaticamente dalle vendite e manutenzioni. Non devo più inserire nulla manualmente.' },
    { name: 'Laura Martelli', text: 'Uso il software per gestire due officine. Posso vedere tutto da un\'unica dashboard e controllare entrambe le sedi senza problemi. Molto comodo.' },
    { name: 'Fabio Gatti', text: 'Il sistema di tracciamento manutenzioni è perfetto. Vedo lo stato di ogni intervento in tempo reale e posso comunicare ai clienti quando sarà pronto. Professionalità al top.' },
    { name: 'Michela Colombo', text: 'La stampa dei preventivi è professionale. I clienti rimangono impressionati dalla chiarezza e dettaglio. Ha migliorato molto l\'immagine della mia officina.' }
  ];

  const getReviewCardWidth = () => {
    if (isMobile) return 280; // Card width + gap
    return 380; // Card width (350) + gap (30)
  };

  const getReviewsPerView = () => {
    if (isMobile) return 1;
    return 3;
  };

  useEffect(() => {
    if (!isHoveringReviews) {
      autoScrollIntervalRef.current = setInterval(() => {
        setReviewScrollPosition((prev) => {
          const cardWidth = getReviewCardWidth();
          const perView = getReviewsPerView();
          const maxScroll = Math.max(0, (reviews.length - perView) * cardWidth);
          const nextPosition = prev + cardWidth;
          return nextPosition > maxScroll ? 0 : nextPosition;
        });
      }, 3000); // Scorre ogni 3 secondi
    } else {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    }

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [isHoveringReviews, reviews.length, isMobile]);

  const handleReviewScroll = (direction) => {
    const cardWidth = getReviewCardWidth();
    const perView = getReviewsPerView();
    const scrollAmount = cardWidth * perView;
    setReviewScrollPosition((prev) => {
      const maxScroll = Math.max(0, (reviews.length - perView) * cardWidth);
      if (direction === 'left') {
        return Math.max(0, prev - scrollAmount);
      } else {
        const nextPosition = prev + scrollAmount;
        return nextPosition > maxScroll ? 0 : nextPosition;
      }
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 50%, ${brandColors.primary} 0%, rgba(0,0,0,0.8) 98%, rgba(0,0,0,1) 100%)`,
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <img 
                  src="/logo192.png" 
                  alt="Mobility Hub SaaS Logo" 
                  style={{ 
                    height: '60px', 
                    width: 'auto',
                    marginRight: '20px'
                  }} 
                />
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    fontWeight: 700,
                    lineHeight: 1.2
                  }}
                >
                  Mobility Hub SaaS
                </Typography>
              </Box>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  fontWeight: 400,
                  mb: 4,
                  opacity: 0.95
                }}
              >
                Software Gestionale Completo per Officine Bici
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6
                }}
              >
                Gestisci clienti, manutenzioni, magazzino, vendite e contabilità in un'unica piattaforma integrata. 
                Semplifica le operazioni quotidiane della tua officina con strumenti potenti e facili da usare.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: 'white',
                    color: brandColors.primary,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      transform: 'translateY(-2px)',
                      boxShadow: 6
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Accedi al Software
                </Button>
                <Button
                  component={Link}
                  to="/pricing"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  Scopri i Prezzi
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  position: 'relative'
                }}
              >
                <img 
                  src="/Dashboard.png" 
                  alt="Dashboard Mobility Hub SaaS" 
                  style={{ 
                    width: '100%',
                    maxWidth: '600px',
                    height: 'auto',
                    objectFit: 'contain',
                    opacity: 0.9
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: 'text.primary'
            }}
          >
            Funzionalità Principali
          </Typography>
          <Typography
            variant="h6"
            component="p"
            align="center"
            sx={{
              mb: 8,
              color: 'text.secondary',
              maxWidth: 700,
              mx: 'auto'
            }}
          >
            Tutto ciò di cui hai bisogno per gestire la tua officina in modo efficiente
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  onClick={() => handleFeatureClick(feature)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                      borderColor: feature.color
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box
                      sx={{
                        color: feature.color,
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        textAlign: 'center',
                        color: 'text.primary'
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        textAlign: 'center',
                        lineHeight: 1.7
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          variant="h3"
          component="h2"
          align="center"
          sx={{
            fontWeight: 700,
            mb: 1,
            color: 'text.primary'
          }}
        >
          Perché Scegliere Mobility Hub SaaS?
        </Typography>
        <Typography
          variant="h6"
          component="p"
          align="center"
          sx={{
            mb: 6,
            color: 'text.secondary',
            maxWidth: 600,
            mx: 'auto'
          }}
        >
          Una soluzione completa che semplifica la gestione della tua officina
        </Typography>
        <Grid container spacing={4}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    {benefit.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Feature Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        {selectedFeature && (
          <>
            <DialogTitle
              sx={{
                bgcolor: selectedFeature.color,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ color: 'white' }}>
                  {selectedFeature.icon}
                </Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                  {selectedFeature.title}
                </Typography>
              </Box>
              <IconButton
                onClick={handleCloseDialog}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, bgcolor: 'white', color: '#000' }}>
              {selectedFeature.image && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <img 
                    src={selectedFeature.image} 
                    alt={selectedFeature.title}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: '300px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                </Box>
              )}
              <Typography variant="body1" paragraph sx={{ mb: 3, fontSize: '1.1rem', color: '#000 !important' }}>
                {selectedFeature.description}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: '#000 !important' }}>
                Caratteristiche Principali:
              </Typography>
              <List sx={{ color: '#000' }}>
                {selectedFeature.details.map((detail, index) => (
                  <ListItem key={index} sx={{ py: 1, color: '#000' }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: selectedFeature.color }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={detail}
                      primaryTypographyProps={{
                        sx: { fontSize: '1rem', color: '#000 !important' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{
                  borderColor: selectedFeature.color,
                  color: selectedFeature.color,
                  '&:hover': {
                    borderColor: selectedFeature.color,
                    bgcolor: `${selectedFeature.color}10`
                  }
                }}
              >
                Chiudi
              </Button>
              <Button
                component={Link}
                to="/pricing"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: selectedFeature.color,
                  '&:hover': {
                    bgcolor: selectedFeature.color,
                    opacity: 0.9
                  }
                }}
              >
                Prova Ora
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reviews Section */}
      <Box sx={{ bgcolor: 'background.default', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: 'text.primary'
            }}
          >
            Cosa Dicono i Nostri Clienti
          </Typography>
          <Typography
            variant="h6"
            component="p"
            align="center"
            sx={{
              mb: 6,
              color: 'text.secondary',
              maxWidth: 700,
              mx: 'auto'
            }}
          >
            Migliaia di officine si affidano a Mobility Hub SaaS per gestire le proprie attività
          </Typography>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              overflow: 'hidden'
            }}
            onMouseEnter={() => setIsHoveringReviews(true)}
            onMouseLeave={() => setIsHoveringReviews(false)}
          >
            <IconButton
              onClick={() => handleReviewScroll('left')}
              sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                bgcolor: 'white',
                boxShadow: 3,
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box
              ref={reviewsContainerRef}
              sx={{
                display: 'flex',
                gap: 3,
                transition: 'transform 0.5s ease-in-out',
                transform: `translateX(-${reviewScrollPosition}px)`,
                px: 8
              }}
            >
              {reviews.map((review, index) => (
                <Card
                  key={index}
                  sx={{
                    minWidth: { xs: 280, sm: 320, md: 350 },
                    maxWidth: { xs: 280, sm: 320, md: 350 },
                    flexShrink: 0,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} sx={{ color: '#ffc107', fontSize: 20 }} />
                    ))}
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 2,
                      flexGrow: 1,
                      color: 'text.primary',
                      fontStyle: 'italic',
                      lineHeight: 1.6
                    }}
                  >
                    "{review.text}"
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}
                  >
                    — {review.name}
                  </Typography>
                </Card>
              ))}
            </Box>
            <IconButton
              onClick={() => handleReviewScroll('right')}
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                bgcolor: 'white',
                boxShadow: 3,
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 50%, ${brandColors.primary} 0%, rgba(0,0,0,0.8) 98%, rgba(0,0,0,1) 100%)`,
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h3"
            component="h2"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 3
            }}
          >
            Pronto a Iniziare?
          </Typography>
          <Typography
            variant="h6"
            component="p"
            align="center"
            sx={{
              mb: 4,
              opacity: 0.95
            }}
          >
            Iscriviti ora e scopri come Mobility Hub SaaS può trasformare la gestione della tua officina
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              component={Link}
              to="/pricing"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: 'white',
                color: brandColors.primary,
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: 8
                },
                transition: 'all 0.3s ease'
              }}
            >
              Iscriviti
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: brandColors.dark, color: 'grey.300', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img 
                  src="/logo192.png" 
                  alt="Mobility Hub SaaS Logo" 
                  style={{ 
                    height: '40px', 
                    width: 'auto',
                    marginRight: '15px',
                    filter: 'brightness(0) invert(1)'
                  }} 
                />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Mobility Hub SaaS
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Software gestionale completo per officine e noleggi di micro-mobilità urbana.
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                © {new Date().getFullYear()} Mobility Hub SaaS. Tutti i diritti riservati.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
                Contatti
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Email: driviomobility@gmail.com
              </Typography>
              <Typography variant="body2">
                Supporto tecnico disponibile
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
